import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

/* =========================================================================
   DieticianConsultation.js
   Single-file React implementation (Functional Component + Hooks only)

   INTEGRATION WITH PatientTechnicianDashboard.js
   -------------------------------------------------------------------------
   This form is reached the same way vitals / audiometry / etc. are: the
   dashboard's handleServiceClick() does:

     navigate(route, {
       state: { patientId, patientName, technicianId, serviceId, isEditing }
     });

   So this component does NOT fetch or show a patient dropdown -- the
   patient is already chosen on the dashboard. To wire it up:

   1. In PatientTechnicianDashboard.js, add to `serviceRoutes`:
        'dietician consultation': '/dietician-consultation',

   2. In your router config, add:
        <Route path="/dietician-consultation" element={<DieticianConsultation />} />

   API BASE (confirmed live 2026-07-11):
     GET https://cbackend.xraidigital.com/api/technician/
     -> { ..., "dietician-consultation": ".../api/technician/dietician-consultation/" }

   `patient` field sent to the backend = location.state.patientId
   (i.e. patient.unique_patient_id, same value used elsewhere in the
   dashboard for submitTechnicianServiceDone). Change PATIENT_ID_FIELD
   below if your serializer expects a different identifier.

   NOTE ON FIELD NAMES:
   Section field keys (snake_case) are a best-guess mapping from your
   requirements doc -- see `emptyForm` below. If your serializer differs,
   that object is the only place to edit.
   ========================================================================= */

// ---------------------------------------------------------------------------
// CONFIG
// ---------------------------------------------------------------------------
const TECHNICIAN_API_BASE = "https://cbackend.xraidigital.com/api/technician";
const DIETICIAN_CONSULTATION_URL = `${TECHNICIAN_API_BASE}/dietician-consultation/`;

const DIET_PREFERENCE_OPTIONS = [
  { value: "veg", label: "Veg" },
  { value: "non_veg", label: "Non Veg" },
  { value: "eggetarian", label: "Eggetarian" },
];
const PHYSICAL_ACTIVITY_OPTIONS = [
  { value: "sedentary", label: "Sedentary" },
  { value: "moderate", label: "Moderate" },
  { value: "heavy", label: "Heavy" },
];
const NUTRITIONAL_STATUS_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "underweight", label: "Underweight" },
  { value: "overweight", label: "Overweight" },
  { value: "obese", label: "Obese" },
];
// const CONSULTATION_STATUS_OPTIONS = [
//   { value: "pending", label: "Pending" },
//   { value: "completed", label: "Completed" },
// ];
const BREAKFAST_OPTIONS = [
    { value: "daily", label: "Daily" },
    { value: "occasionally", label: "Occasionally" },
    { value: "skip", label: "Skip" },
];

const MEDICAL_HISTORY_CHECKBOXES = [
  { key: "diabetes", label: "Diabetes" },
  { key: "hypertension", label: "Hypertension" },
  { key: "thyroid_disorder", label: "Thyroid Disorder" },
  { key: "heart_disease", label: "Heart Disease" },
  { key: "kidney_disease", label: "Kidney Disease" },
  { key: "liver_disease", label: "Liver Disease" },
];

const emptyForm = {
  patient: "",
  consultation_date: "",

  diabetes: false,
  hypertension: false,
  thyroid_disorder: false,
  heart_disease: false,
  kidney_disease: false,
  liver_disease: false,
  food_allergy: false,
  food_allergy_details: "",
  other_medical_conditions: "",

  diet_preference: "",
  smoking: false,
  alcohol_consumption: "",
  physical_activity: "",
  water_intake: "",
  sleep_duration: "",

  breakfast: "",
  lunch_timing: "",
  dinner_timing: "",
  fruits_intake: "",
  vegetables_intake: "",
  fast_food_frequency: "",
  sugary_drinks: false,

  nutritional_status: "",
  primary_concern: "",
  assessment_notes: "",

  diet_plan: "",
  exercise_recommendation: "",
  follow_up_required: false,
  follow_up_duration: "",

  remarks: "",
  // consultation_status: "pending",
};

// ---------------------------------------------------------------------------
// STYLES (single style object, blue + white healthcare theme)
// ---------------------------------------------------------------------------
const COLORS = {
  primary: "#0B5ED7",
  primaryDark: "#084298",
  primaryLight: "#E7F1FF",
  accent: "#12B886",
  danger: "#DC3545",
  border: "#D6E4F0",
  bg: "#F4F8FC",
  card: "#FFFFFF",
  text: "#1B2733",
  textMuted: "#5C7285",
};

const styles = {
  page: {
    minHeight: "100vh",
    background: COLORS.bg,
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif",
    color: COLORS.text,
    paddingBottom: 110,
  },
  header: {
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
    color: "#fff",
    padding: "22px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 10px rgba(11,94,215,0.25)",
    flexWrap: "wrap",
    gap: 12,
  },
  headerTitleWrap: { display: "flex", alignItems: "center", gap: 12 },
  headerTitle: { fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: 0.3 },
  headerSubtitle: { fontSize: 13, opacity: 0.85, marginTop: 2 },
  backBtn: {
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.4)",
    borderRadius: 10,
    padding: "9px 16px",
    cursor: "pointer",
    fontSize: 13.5,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  container: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "24px 20px",
  },
  card: {
    background: COLORS.card,
    borderRadius: 16,
    boxShadow: "0 4px 18px rgba(20,50,90,0.08)",
    border: `1px solid ${COLORS.border}`,
    marginBottom: 22,
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 20px",
    borderBottom: `1px solid ${COLORS.border}`,
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: COLORS.primaryLight,
  },
  cardHeaderTitle: { fontSize: 16, fontWeight: 700, color: COLORS.primaryDark, margin: 0 },
  cardBody: { padding: "20px" },
  patientBar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
  },
  field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  label: { fontSize: 13, fontWeight: 600, color: COLORS.textMuted },
  required: { color: COLORS.danger, marginLeft: 3 },
  input: {
    padding: "10px 12px",
    borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`,
    fontSize: 14,
    background: "#fff",
    color: COLORS.text,
    outline: "none",
    transition: "border-color .15s",
    width: "100%",
    boxSizing: "border-box",
  },
  inputError: { borderColor: COLORS.danger },
  inputReadonly: { background: "#F0F4F8", color: COLORS.textMuted },
  select: {
    padding: "10px 12px",
    borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`,
    fontSize: 14,
    background: "#fff",
    color: COLORS.text,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "10px 12px",
    borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`,
    fontSize: 14,
    minHeight: 80,
    resize: "vertical",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  errorText: { color: COLORS.danger, fontSize: 12, fontWeight: 500 },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: "#FAFCFF",
  },
  checkbox: { width: 17, height: 17, accentColor: COLORS.primary, cursor: "pointer" },
  accordionButton: {
    width: "100%",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 0,
    fontFamily: "inherit",
  },
  chevron: (open) => ({
    transition: "transform .2s ease",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    color: COLORS.primaryDark,
  }),
  stickyBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#fff",
    borderTop: `1px solid ${COLORS.border}`,
    boxShadow: "0 -4px 16px rgba(20,50,90,0.10)",
    padding: "14px 20px",
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    zIndex: 50,
  },
  btn: {
    padding: "11px 22px",
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 14,
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition: "opacity .15s, transform .1s",
  },
  btnPrimary: { background: COLORS.primary, color: "#fff" },
  btnPrimaryDisabled: { background: "#8FB8EA", color: "#fff", cursor: "not-allowed" },
  btnSecondary: { background: "#fff", color: COLORS.primary, border: `1.5px solid ${COLORS.primary}` },
  btnGhost: { background: "transparent", color: COLORS.textMuted, border: `1px solid ${COLORS.border}` },
  btnSuccess: { background: COLORS.accent, color: "#fff" },
  btnSm: { padding: "6px 10px", fontSize: 12, borderRadius: 8 },
  banner: (type) => ({
    padding: "14px 18px",
    borderRadius: 12,
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    fontWeight: 500,
    background: type === "success" ? "#E7F9F1" : type === "error" ? "#FDECEE" : COLORS.primaryLight,
    color: type === "success" ? "#0F8A55" : type === "error" ? COLORS.danger : COLORS.primaryDark,
    border: `1px solid ${type === "success" ? "#BEEBD7" : type === "error" ? "#F6C4CA" : COLORS.border}`,
  }),
  emptyState: { textAlign: "center", padding: "40px 20px", color: COLORS.textMuted },
};

// ---------------------------------------------------------------------------
// SMALL SVG ICONS (no external icon library dependency)
// ---------------------------------------------------------------------------
const Icon = {
  Chevron: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Download: (p) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M12 3v12m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Leaf: (p) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M4 20c8 0 14-4 16-16C10 4 4 10 4 20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M6 18C10 14 13 11 18 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Check: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Alert: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  ),
  Back: (p) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M19 12H5m0 0 6 6m-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const SpinKeyframes = () => <style>{`@keyframes dc-spin { to { transform: rotate(360deg); } }`}</style>;

// ---------------------------------------------------------------------------
// AXIOS INSTANCE
// ---------------------------------------------------------------------------
function useApi() {
  return useMemo(() => {
    const instance = axios.create();
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("token");
      config.headers = { ...config.headers, Authorization: `Token ${token}` };
      return config;
    });
    return instance;
  }, []);
}

// ---------------------------------------------------------------------------
// FORM FIELD HELPERS
// ---------------------------------------------------------------------------
const Field = ({ label, required, error, children }) => (
  <div style={styles.field}>
    <label style={styles.label}>
      {label}
      {required && <span style={styles.required}>*</span>}
    </label>
    {children}
    {error && <span style={styles.errorText}>{error}</span>}
  </div>
);

const TextInput = ({ value, onChange, error, type = "text", placeholder, readOnly, ...rest }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    style={{ ...styles.input, ...(error ? styles.inputError : {}), ...(readOnly ? styles.inputReadonly : {}) }}
    {...rest}
  />
);

const TextArea = ({ value, onChange, error, placeholder, readOnly }) => (
  <textarea
    value={value ?? ""}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    style={{ ...styles.textarea, ...(error ? styles.inputError : {}), ...(readOnly ? styles.inputReadonly : {}) }}
  />
);

const SelectInput = ({ value, onChange, options, error, placeholder, disabled }) => (
  <select
    value={value ?? ""}
    onChange={onChange}
    disabled={disabled}
    style={{ ...styles.select, ...(error ? styles.inputError : {}) }}
  >
    <option value="">{placeholder || "Select"}</option>
    {options.map((opt) => (
        <option
            key={opt.value}
            value={opt.value}
        >
            {opt.label}
        </option>
    ))}
  </select>
);

// ---------------------------------------------------------------------------
// ACCORDION SECTION
// ---------------------------------------------------------------------------
const AccordionSection = ({ title, sectionKey, openSections, toggleSection, children }) => {
  const open = !!openSections[sectionKey];
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <button type="button" style={styles.accordionButton} onClick={() => toggleSection(sectionKey)}>
          <h3 style={styles.cardHeaderTitle}>{title}</h3>
          <Icon.Chevron style={styles.chevron(open)} />
        </button>
      </div>
      {open && <div style={styles.cardBody}>{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
export default function DieticianConsultation() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  // Patient / navigation context passed in from PatientTechnicianDashboard
  const {
    patientId,
    patientName,
    technicianId,
    serviceId,
    isEditing: isEditingFlag,
  } = location.state || {};

  // Form state
  const [formData, setFormData] = useState({ ...emptyForm, patient: patientId || "" });
  const [editingId, setEditingId] = useState(null); // dietician-consultation record id (if updating)
  const [errors, setErrors] = useState({});

  // Loading / saving flags
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [openSections, setOpenSections] = useState({
    consultationDate: true,
    medicalHistory: true,
    lifestyle: false,
    eating: false,
    assessment: false,
    advice: false,
    consultationInfo: false,
  });
  const [banner, setBanner] = useState(null); // { type: 'success'|'error', text, pdfUrl }
  const [contextError, setContextError] = useState("");

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // -------------------------------------------------------------------------
  // Guard: this form requires patient context from the dashboard
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!patientId) {
      setContextError(
        "No patient selected. Please open this form from a patient's Dietician Consultation entry on the dashboard."
      );
      setLoadingExisting(false);
    }
  }, [patientId]);

  // -------------------------------------------------------------------------
  // Load existing consultation for this patient (if any) -- prefill + PATCH mode
  // -------------------------------------------------------------------------
  const fetchExisting = useCallback(async () => {
    if (!patientId) return;
    setLoadingExisting(true);
    try {
      const res = await api.get(DIETICIAN_CONSULTATION_URL, { params: { patient: patientId } });
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      const existing = data.find(
          (r) => String(r.patient) === String(patientId)
      );

      if (existing) {
          setFormData({
              ...emptyForm,
              ...existing,
              patient: patientId,
          });

          setEditingId(existing.id);
      } else {
          setFormData({
              ...emptyForm,
              patient: patientId,
          });

          setEditingId(null);
      }
      if (existing) {
        setFormData({ ...emptyForm, ...existing, patient: patientId });
        setEditingId(existing.id);
        setOpenSections({
          consultationDate: true,
          medicalHistory: true,
          lifestyle: true,
          eating: true,
          assessment: true,
          advice: true,
          consultationInfo: true,
        });
      } else if (isEditingFlag) {
        // Dashboard flagged this as "editing" but no record was found --
        // fall back gracefully to a fresh form for this patient.
        setFormData({ ...emptyForm, patient: patientId });
        setEditingId(null);
      }
    } catch (err) {
      // No existing record (404) or list fetch failed -- proceed as a new consultation.
      setFormData((prev) => ({ ...prev, patient: patientId }));
    } finally {
      setLoadingExisting(false);
    }
  }, [api, patientId, isEditingFlag]);

  useEffect(() => {
    if (patientId) fetchExisting();
  }, [patientId, fetchExisting]);

  // -------------------------------------------------------------------------
  // FORM HANDLERS
  // -------------------------------------------------------------------------
  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleCheckbox = (key) => (e) => setField(key, e.target.checked);
  const handleText = (key) => (e) => setField(key, e.target.value);

  const validate = () => {
    const errs = {};
    if (!formData.consultation_date) errs.consultation_date = "Consultation date is required.";
    if (!formData.diet_preference) errs.diet_preference = "Please select a diet preference.";
    if (!formData.physical_activity) errs.physical_activity = "Please select physical activity level.";
    if (!formData.nutritional_status) errs.nutritional_status = "Please select nutritional status.";
    // if (!formData.consultation_status) errs.consultation_status = "Please select consultation status.";
    if (formData.food_allergy && !formData.food_allergy_details.trim()) {
      errs.food_allergy_details = "Please describe the food allergy.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Build payload -- NEVER include `dietician`, backend assigns via request.user
  const buildPayload = () => {
    const payload = { ...formData, patient: patientId };
    delete payload.dietician; // safety: guarantee it's never sent
    return payload;
  };

  const handleSave = async () => {
    setBanner(null);
    if (!patientId) {
      setBanner({ type: "error", text: "No patient selected -- cannot save." });
      return;
    }
    if (!validate()) {
      setBanner({ type: "error", text: "Please fix the highlighted fields before saving." });
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (editingId) {
        res = await api.patch(`${DIETICIAN_CONSULTATION_URL}${editingId}/`, payload);
      } else {
        res = await api.post(DIETICIAN_CONSULTATION_URL, payload);
      }
      const saved = res.data;
      setEditingId(saved.id || editingId);
      setBanner({
        type: "success",
        text: "Consultation Saved Successfully",
        pdfUrl: saved.pdf_report || saved.pdf_url || saved.report_url || null,
      });
      // --------------------------------------------
        // Mark Technician Service Done
        // --------------------------------------------
        if (patientId && technicianId && serviceId) {
        await api.post(
            `${TECHNICIAN_API_BASE}/submit/`,
            {
            patient_id: patientId,
            technician_id: technicianId,
            service_id: serviceId,
            }
        );
        }
        setTimeout(() => {
        navigate(-1);
        }, 1000);
    } catch (err) {
      const serverMsg =
        err?.response?.data && typeof err.response.data === "object"
          ? Object.entries(err.response.data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join(" | ")
          : "Something went wrong while saving. Please try again.";
      setBanner({ type: "error", text: serverMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (url) => {
    if (url) window.open(url, "_blank");
  };

  const handleBack = () => navigate(-1);

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  if (contextError) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.headerTitleWrap}>
            <Icon.Leaf />
            <div>
              <h1 style={styles.headerTitle}>Dietician Consultation</h1>
            </div>
          </div>
          <button type="button" style={styles.backBtn} onClick={handleBack}>
            <Icon.Back /> Back
          </button>
        </header>
        <div style={styles.container}>
          <div style={styles.banner("error")}>
            <Icon.Alert /> {contextError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <SpinKeyframes />

      <header style={styles.header}>
        <div style={styles.headerTitleWrap}>
          <Icon.Leaf />
          <div>
            <h1 style={styles.headerTitle}>Dietician Consultation</h1>
            <div style={styles.headerSubtitle}>Nutrition &amp; Lifestyle Assessment</div>
          </div>
        </div>
        <button type="button" style={styles.backBtn} onClick={handleBack}>
          <Icon.Back /> Back to Patients
        </button>
      </header>

      <div style={styles.container}>
        {banner && (
          <div style={styles.banner(banner.type)}>
            {banner.type === "success" ? <Icon.Check /> : <Icon.Alert />}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span>{banner.text}</span>
              {banner.type === "success" && banner.pdfUrl && (
                <button
                  type="button"
                  onClick={() => handleDownload(banner.pdfUrl)}
                  style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSm, width: "fit-content" }}
                >
                  <Icon.Download /> Download Report
                </button>
              )}
            </div>
          </div>
        )}

        {/* ---------------- PATIENT CONTEXT (read-only, from dashboard) ---------------- */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardHeaderTitle}>Patient</h3>
            {editingId && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  fontWeight: 700,
                  color: COLORS.primaryDark,
                  background: "#fff",
                  border: `1px solid ${COLORS.border}`,
                  padding: "3px 10px",
                  borderRadius: 999,
                }}
              >
                Editing existing consultation
              </span>
            )}
          </div>
          <div style={styles.cardBody}>
            {loadingExisting ? (
              <div style={styles.emptyState}>Loading consultation data…</div>
            ) : (
              <div style={styles.patientBar}>
                <Field label="Patient Name">
                  <TextInput value={patientName} readOnly placeholder="—" />
                </Field>
                <Field label="Patient ID">
                  <TextInput value={patientId} readOnly placeholder="—" />
                </Field>
              </div>
            )}
          </div>
        </div>

        {!loadingExisting && (
          <>
            {/* ---------------- SECTION 1: CONSULTATION DATE ---------------- */}
            <AccordionSection
              title="Consultation Date"
              sectionKey="consultationDate"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.grid2}>
                <Field label="Consultation Date" required error={errors.consultation_date}>
                  <TextInput
                    type="date"
                    value={formData.consultation_date}
                    onChange={handleText("consultation_date")}
                    error={errors.consultation_date}
                  />
                </Field>
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 2: MEDICAL HISTORY ---------------- */}
            <AccordionSection
              title="Medical History"
              sectionKey="medicalHistory"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.checkboxGrid}>
                {MEDICAL_HISTORY_CHECKBOXES.map((c) => (
                  <label key={c.key} style={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={!!formData[c.key]}
                      onChange={handleCheckbox(c.key)}
                    />
                    {c.label}
                  </label>
                ))}
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={!!formData.food_allergy}
                    onChange={handleCheckbox("food_allergy")}
                  />
                  Food Allergy
                </label>
              </div>

              <div style={{ ...styles.grid2, marginTop: 16 }}>
                {formData.food_allergy && (
                  <Field label="Food Allergy Details" required error={errors.food_allergy_details}>
                    <TextInput
                      value={formData.food_allergy_details}
                      onChange={handleText("food_allergy_details")}
                      error={errors.food_allergy_details}
                      placeholder="e.g. peanuts, shellfish"
                    />
                  </Field>
                )}
                <Field label="Other Medical Conditions">
                  <TextArea
                    value={formData.other_medical_conditions}
                    onChange={handleText("other_medical_conditions")}
                    placeholder="Any other relevant medical conditions"
                  />
                </Field>
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 3: LIFESTYLE ASSESSMENT ---------------- */}
            <AccordionSection
              title="Lifestyle Assessment"
              sectionKey="lifestyle"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.grid2}>
                <Field label="Diet Preference" required error={errors.diet_preference}>
                  <SelectInput
                    value={formData.diet_preference}
                    onChange={handleText("diet_preference")}
                    options={DIET_PREFERENCE_OPTIONS}
                    error={errors.diet_preference}
                  />
                </Field>
                <Field label="Physical Activity" required error={errors.physical_activity}>
                  <SelectInput
                    value={formData.physical_activity}
                    onChange={handleText("physical_activity")}
                    options={PHYSICAL_ACTIVITY_OPTIONS}
                    error={errors.physical_activity}
                  />
                </Field>
                <Field label="Water Intake (litres/day)">
                  <TextInput type="number" value={formData.water_intake} onChange={handleText("water_intake")} />
                </Field>
                <Field label="Sleep Duration (hours)">
                  <TextInput type="number" value={formData.sleep_duration} onChange={handleText("sleep_duration")} />
                </Field>
                <Field label="Alcohol Consumption">
                  <TextInput
                    value={formData.alcohol_consumption}
                    onChange={handleText("alcohol_consumption")}
                    placeholder="e.g. occasional, none, weekly"
                  />
                </Field>
                <label style={{ ...styles.checkboxRow, alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={!!formData.smoking}
                    onChange={handleCheckbox("smoking")}
                  />
                  Smoking
                </label>
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 4: EATING HABITS ---------------- */}
            <AccordionSection
              title="Eating Habits"
              sectionKey="eating"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.grid2}>
                <SelectInput
                    value={formData.breakfast}
                    onChange={handleText("breakfast")}
                    options={BREAKFAST_OPTIONS}
                />
                <Field label="Lunch Timing">
                  <TextInput type="time" value={formData.lunch_timing} onChange={handleText("lunch_timing")} />
                </Field>
                <Field label="Dinner Timing">
                  <TextInput type="time" value={formData.dinner_timing} onChange={handleText("dinner_timing")} />
                </Field>
                <Field label="Fruits Intake">
                  <TextInput value={formData.fruits_intake} onChange={handleText("fruits_intake")} placeholder="e.g. 2 servings/day" />
                </Field>
                <Field label="Vegetables Intake">
                  <TextInput value={formData.vegetables_intake} onChange={handleText("vegetables_intake")} placeholder="e.g. 3 servings/day" />
                </Field>
                <Field label="Fast Food Frequency">
                  <TextInput value={formData.fast_food_frequency} onChange={handleText("fast_food_frequency")} placeholder="e.g. twice a week" />
                </Field>
                <label style={styles.checkboxRow}>
                    <input
                        type="checkbox"
                        checked={formData.sugary_drinks}
                        onChange={handleCheckbox("sugary_drinks")}
                    />
                    Sugary Drinks
                </label>
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 5: DIETICIAN ASSESSMENT ---------------- */}
            <AccordionSection
              title="Dietician Assessment"
              sectionKey="assessment"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.grid2}>
                <Field label="Nutritional Status" required error={errors.nutritional_status}>
                  <SelectInput
                    value={formData.nutritional_status}
                    onChange={handleText("nutritional_status")}
                    options={NUTRITIONAL_STATUS_OPTIONS}
                    error={errors.nutritional_status}
                  />
                </Field>
                <Field label="Primary Concern">
                  <TextInput value={formData.primary_concern} onChange={handleText("primary_concern")} />
                </Field>
              </div>
              <div style={{ marginTop: 16 }}>
                <Field label="Assessment Notes">
                  <TextArea value={formData.assessment_notes} onChange={handleText("assessment_notes")} />
                </Field>
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 6: DIETICIAN ADVICE ---------------- */}
            <AccordionSection
              title="Dietician Advice"
              sectionKey="advice"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div style={styles.grid2}>
                <Field label="Diet Plan">
                  <TextArea value={formData.diet_plan} onChange={handleText("diet_plan")} />
                </Field>
                <Field label="Exercise Recommendation">
                  <TextArea value={formData.exercise_recommendation} onChange={handleText("exercise_recommendation")} />
                </Field>
              </div>
              <div style={{ ...styles.grid2, marginTop: 16 }}>
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={!!formData.follow_up_required}
                    onChange={handleCheckbox("follow_up_required")}
                  />
                  Follow Up Required
                </label>
                {formData.follow_up_required && (
                  <Field label="Follow Up Duration">
                    <TextInput
                      value={formData.follow_up_duration}
                      onChange={handleText("follow_up_duration")}
                      placeholder="e.g. 2 weeks"
                    />
                  </Field>
                )}
              </div>
            </AccordionSection>

            {/* ---------------- SECTION 7: CONSULTATION ---------------- */}
            <AccordionSection
              title="Consultation"
              sectionKey="consultationInfo"
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <Field label="Remarks">
                <TextArea
                    value={formData.remarks}
                    onChange={handleText("remarks")}
                />
            </Field>
            </AccordionSection>
          </>
        )}
      </div>

      {/* ---------------- STICKY SAVE BAR ---------------- */}
      {!loadingExisting && (
        <div style={styles.stickyBar}>
          <button type="button" onClick={handleBack} style={{ ...styles.btn, ...styles.btnSecondary }} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ ...styles.btn, ...(saving ? styles.btnPrimaryDisabled : styles.btnPrimary) }}
          >
            {saving && <span style={styles.spinner} />}
            {saving ? "Saving..." : editingId ? "Update" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}